// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
// ── AsyncLocalStorage (Node.js / Cloudflare Workers) ──────────────────────────
let _asyncLocalStorage = null;
let _AlsConstructor = null;
try {
    // Dynamic require so the import doesn't break browser bundles.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const als = require('node:async_hooks');
    _AlsConstructor = als.AsyncLocalStorage;
    _asyncLocalStorage = new _AlsConstructor();
}
catch {
    // Not available — fall back to module-level context below.
}
// ── Fallback: module-level context (browser / single-thread) ──────────────────
let _moduleCtx = {};
function getStore() {
    if (_asyncLocalStorage) {
        return _asyncLocalStorage.getStore() ?? _moduleCtx;
    }
    return _moduleCtx;
}
function ensureStore() {
    if (_asyncLocalStorage) {
        const store = _asyncLocalStorage.getStore();
        if (store)
            return store;
        const next = { ..._moduleCtx };
        _asyncLocalStorage.enterWith(next);
        return next;
    }
    return _moduleCtx;
}
/**
 * Bind key/value pairs into the current context.
 * These fields are merged into every log record emitted after this call.
 */
export function bindContext(values) {
    const store = ensureStore();
    Object.assign(store, values);
}
/**
 * Remove specific keys from the current context.
 */
export function unbindContext(...keys) {
    const store = ensureStore();
    for (const k of keys)
        delete store[k];
}
/**
 * Clear all context bindings.
 */
export function clearContext() {
    if (_asyncLocalStorage) {
        const store = _asyncLocalStorage.getStore();
        if (store) {
            for (const k of Object.keys(store))
                delete store[k];
            return;
        }
    }
    _moduleCtx = {};
}
/**
 * Return a snapshot of the current context (no side effects).
 */
export function getContext() {
    return { ...getStore() };
}
/**
 * Run fn with additional context values scoped to its execution.
 * In Node.js, uses AsyncLocalStorage so the bindings are isolated per-request.
 * In browser, temporarily binds then restores the previous state.
 * Mirrors Python: contextvars copy_context().run(fn) pattern.
 */
export function runWithContext(values, fn) {
    if (_asyncLocalStorage) {
        const inherited = { ...getStore(), ...values };
        return _asyncLocalStorage.run(inherited, fn);
    }
    const prev = { ...getStore() };
    bindContext(values);
    try {
        return fn();
    }
    finally {
        _moduleCtx = prev;
    }
}
/**
 * Bind a session ID that propagates across all telemetry events.
 */
export function bindSessionContext(sessionId) {
    bindContext({ session_id: sessionId });
}
/**
 * Return the current session ID, or null if not set.
 */
export function getSessionId() {
    const sessionId = getStore()['session_id'];
    return typeof sessionId === 'string' ? sessionId : null;
}
/**
 * Clear the session ID.
 */
export function clearSessionContext() {
    unbindContext('session_id');
}
/** Reset to empty context (used in tests). */
export function _resetContext() {
    // Recreate ALS so no enterWith-seeded store leaks between tests.
    // The null branch is only reachable in environments without node:async_hooks (e.g. browsers).
    /* v8 ignore next */
    _asyncLocalStorage = _AlsConstructor ? new _AlsConstructor() : null;
    _moduleCtx = {};
}
/** Disable AsyncLocalStorage for testing the module-level fallback path. */
export function _disableAsyncLocalStorageForTest() {
    const prev = _asyncLocalStorage;
    _asyncLocalStorage = null;
    return prev;
}
/** Re-enable AsyncLocalStorage after testing (pass value from _disable call). */
// Stryker disable next-line BlockStatement: assignment-only body — removing leaves _asyncLocalStorage unchanged which is equivalent when tests always call _resetContext after restore
export function _restoreAsyncLocalStorageForTest(saved) {
    _asyncLocalStorage = saved;
}
